import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import * as Icons from '@nxsys/components/AppIconSvg';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { agencyAction } from '@nxsys/store/slices/agency/reducer/agencySlice';
import { GetAgencyListParams } from '@nxsys/store/slices/agency/types';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getColumns } from './columns';
import { IRELAND } from '@nxsys/store/constants/keys';
import { debounce } from 'lodash';
import { showToast } from '@nxsys/utils/toast';
import { includes } from 'lodash';
import { ExportToCsv } from 'export-to-csv';
import { csvConfigExport } from '@nxsys/utils/export';
import { formatStatus } from '@nxsys/utils';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import AddAgencyModal from './AddAgencyModal';

const AgencyListing = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const [addAgencyModalOpen, setAddAgencyModalOpen] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { agencyList } = useSelector((state: AppState) => state.agencyStore);
  const { loading, list, pagination, queryParams } = agencyList;
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const [searchValue, setSearchValue] = useState('');
  const [selectedRows, setSelectedRows] = useState<IAgency[]>([]);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetAgencyListParams) => {
    const newParams = { ...params };
    dispatch(agencyAction.getAgencyList(newParams));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const exportData = (params: IAgency[]) => {
    if (!params.length) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }

    const formattedData = params.map((item) => ({
      id: item?.id ?? '',
      name: item?.name ?? '',
      status: formatStatus(item?.status) ?? 'Inactive',
      contactName: item?.contactName ?? '',
      email: item?.email ?? '',
      mobile: item?.mobile ?? '',
    }));

    const headers = [
      'Agency ID',
      'Agency Name',
      'Status',
      'Contact Name',
      'Email ID',
      'Phone',
    ];

    const exportName = csvConfigExport('Agency-export', headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(formattedData);
  };

  const handleExportData = () => {
    const rowsSelected = selectedRows.map((row) => row.id);
    const listAgency = { list };

    const dataExport =
      listAgency?.list?.filter((item) => includes(rowsSelected, item?.id)) ??
      [];
    exportData(dataExport);
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const handleExportAll = async () => {
    if (!pagination?.totalDocs || pagination.totalDocs <= 0) {
      showToast({
        message: messages['common.noData'],
        type: 'warning',
      });
      return;
    }

    try {
      setIsDownloading(true);

      const response = await dispatch(
        agencyAction.getAgencyList({
          page: 0,
          size: pagination.totalDocs,
        }),
      ).unwrap();

      exportData(response.data);

      setIsDownloading(false);
    } catch (error: any) {
      setIsDownloading(false);
      showToast({
        message: error?.response?.data?.message || 'Export failed',
        type: 'error',
      });
    }
  };

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['common.agencies']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          onClick={handleExportData}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          loading={isDownloading}
          onClick={handleExportAll}

        />
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          onClick={() => setAddAgencyModalOpen(true)}
          leftIcon={<PlusOutlined />}
        />
      </div>
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== queryParams?.size;
    handleFetchData({
      ...queryParams,
      page: isPageSizeChanged ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleViewDetail = (id: string) => {
    navigate(`/agency/${id}`);
  };

  const isFiltering = !!queryParams?.search;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1200, y: 700 }}
            titleEmpty={messages['assignment.empty']}
            columns={getColumns({
              messages,
              isIreland,
              handleViewDetail,
            })}
            rowSelection={{
              type: 'checkbox',
              onChange: (_selectedRowKeys: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangePagination={onChangePagination}
            noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
            addNewBtnOnClick={() => setAddAgencyModalOpen(true)}
            dataTypeName={messages['common.agency']}
          />
        </AppsContent>
      </AppCard>
      <AddAgencyModal
        isOpen={addAgencyModalOpen}
        onClose={() => setAddAgencyModalOpen(false)}
        onSuccess={() => setAddAgencyModalOpen(false)}
      />
    </AppsContainer>
  );
};

export default AgencyListing;
